import { momentsApi, momentsBearerQuery } from './momentsApi';

export const MEMBER_REFLECTION_ONBOARDING_VIEW_KEY =
  'member.reflection_onboarding@v1';

export interface DismissableViewState {
  key: string;
  dismissedAt: string | null;
}

export type DismissableViewStates = Record<string, DismissableViewState>;

export interface GetDismissableViewStatesArg {
  keys: string[];
}

export interface DismissDismissableViewArg {
  key: string;
}

interface DismissableViewStateWire {
  dismissed_at: string | null;
  key: string;
}

type DismissableViewStatesWire = Record<string, DismissableViewStateWire>;

const mapDismissableViewState = (
  state: DismissableViewStateWire,
): DismissableViewState => ({
  dismissedAt: state.dismissed_at,
  key: state.key,
});

const normalizeDismissableViewStates = (
  states: unknown,
): DismissableViewStates =>
  Object.fromEntries(
    Object.values(states as DismissableViewStatesWire).map((state) => [
      state.key,
      mapDismissableViewState(state),
    ]),
  );

const buildViewStatesPath = (keys: string[]): string =>
  `/api/v1/ui/view-states?${new URLSearchParams({
    keys: keys.join(','),
  }).toString()}`;

export const dismissableViewsApi = momentsApi.injectEndpoints({
  endpoints: (builder) => ({
    dismissDismissableView: builder.mutation<
      DismissableViewState,
      DismissDismissableViewArg
    >({
      onQueryStarted: async ({ key }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            dismissableViewsApi.util.updateQueryData(
              'getDismissableViewStates',
              { keys: [key] },
              (draft) => {
                Object.assign(draft, { [key]: data });
              },
            ),
          );
        } catch {
          // Keep the cached state unchanged when dismissal fails.
        }
      },
      queryFn: async ({ key }, queryApi) =>
        momentsBearerQuery(queryApi, {
          body: { key },
          fallbackData: { dismissedAt: null, key },
          mapResponse: (payload) =>
            mapDismissableViewState(payload as DismissableViewStateWire),
          method: 'POST',
          path: '/api/v1/ui/view-states/dismiss',
          returnErrorOnFailure: true,
        }),
    }),
    getDismissableViewStates: builder.query<
      DismissableViewStates,
      GetDismissableViewStatesArg
    >({
      queryFn: async ({ keys }, queryApi) =>
        momentsBearerQuery(queryApi, {
          fallbackData: {},
          mapResponse: normalizeDismissableViewStates,
          path: buildViewStatesPath(keys),
          returnErrorOnFailure: true,
        }),
    }),
  }),
});

export const {
  useDismissDismissableViewMutation,
  useGetDismissableViewStatesQuery,
} = dismissableViewsApi;
